import { useEffect, useMemo } from "react"

import { formatNumber } from "accounting"
import { Spin } from "antd"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import EmptyComponent from "../../../../Components/Empty"
import Private from "../../../../Components/Private"
import Icon from "../../../../Icon"
import { formatCurrency } from "../../../../Utils/utils"
import { ANALYTICS_SECTION } from "../../redux/analytics.constants"
import { selectSectionData, selectSectionFetching } from "../../redux/analytics.slice"
import { NETWORKS } from "../../utils/breakdowns"
import Section from "../Section"

import styles from "./cardNetworkPanel.module.less"

const META = NETWORKS.reduce((m, n) => {
  m[n.key] = n
  return m
}, {})

// "By card network": one row per scheme with the brand logo (the same registry
// tiles the disputes table's Issuer column uses), name, count + volume sub-label,
// a bar (normalised to the busiest network), the volume share and the win rate.
// The `other` bucket has no logo and is left out, as it always was.
const CardNetworkPanel = ({ getData }) => {
  const { t } = useTranslation()
  const byNetwork = useSelector(selectSectionData(ANALYTICS_SECTION.BY_CARD_NETWORK))
  const fetching = useSelector(selectSectionFetching(ANALYTICS_SECTION.BY_CARD_NETWORK))

  useEffect(() => {
    getData(ANALYTICS_SECTION.BY_CARD_NETWORK)
  }, [getData])

  const rows = useMemo(() => {
    const known = (byNetwork?.result ?? []).filter((row) => META[row.network])
    const maxValue = known.reduce((a, row) => Math.max(a, row.amount), 0) || 1
    return known.map((row) => ({
      ...row,
      icon: META[row.network].icon,
      barWidth: (row.amount / maxValue) * 100
    }))
  }, [byNetwork])

  return (
    <Section
      title={t("By card network")}
      tooltip={t("Dispute volume and win rate by card scheme.")}
    >
      <Spin spinning={fetching}>
        {rows.length ? (
          <div className={styles.list}>
            {rows.map((r) => (
              <div className={styles.row} key={r.network}>
                <span className={styles.brandIcon}>
                  <Icon type={r.icon} />
                </span>
                <div className={styles.main}>
                  <div className={styles.line}>
                    <span className={styles.name}>{r.label}</span>
                    <span className={styles.pct}>{Math.round(r.share)}%</span>
                  </div>
                  <div className={styles.line}>
                    <span className={styles.sub}>
                      {`${formatNumber(r.count, 0, ",")} ${t("disputes")} · `}
                      <Private>{formatCurrency(r.amount, true)}</Private>
                    </span>
                    <span className={styles.win}>
                      {r.win_rate == null
                        ? t("N/A")
                        : t("_rate_% win rate", { rate: Math.round(r.win_rate) })}
                    </span>
                  </div>
                  <div className={styles.bar}>
                    <div className={styles.barFill} style={{ width: `${r.barWidth}%` }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyComponent type="no_data_available" />
        )}
      </Spin>
    </Section>
  )
}

export default CardNetworkPanel
